// The web v2 section map (WEB_V2_BUILD.md §2). S1 owns this file; S2 and SH2
// add their entries here (their reports list them).
//
// - New types: always drawn by their component here.
// - Today's types (cta, contact, faq, links, download, areas) get a v2 look
//   only when the section carries one of its v2 layouts; without one they keep
//   their current component in HomePage.vue, so existing sites (Flat above
//   all) render exactly as before.
//
// Every component takes {section, index, data}: `data` is the page's
// sectionData (src/server/sectionData.js).

import HoursSection from "./HoursSection.vue";
import FindUsSection from "./FindUsSection.vue";
import PriceListSection from "./PriceListSection.vue";
import PackagesSection from "./PackagesSection.vue";
import ReviewsSection from "./ReviewsSection.vue";
import BeforeAfterSection from "./BeforeAfterSection.vue";
import QuoteFormSection from "./QuoteFormSection.vue";
import AccreditationsSection from "./AccreditationsSection.vue";
import HowItWorksSection from "./HowItWorksSection.vue";
import TeamSection from "./TeamSection.vue";
import NumbersSection from "./NumbersSection.vue";
import QuickTasksSection from "./QuickTasksSection.vue";
import CoverCheckSection from "./CoverCheckSection.vue";
import NewsletterSection from "./NewsletterSection.vue";
import LatestPostsSection from "./LatestPostsSection.vue";
import OurWorkSection from "./OurWorkSection.vue";
import BackCardSection from "./BackCardSection.vue";
import CtaV2 from "./CtaV2.vue";
import ContactV2 from "./ContactV2.vue";
import FaqV2 from "./FaqV2.vue";
import LinksV2 from "./LinksV2.vue";
import DownloadV2 from "./DownloadV2.vue";
import AreasV2 from "./AreasV2.vue";
import {V2_REFRESH_LAYOUTS} from "@/utils/v2Layouts.js";

export const V2_SECTIONS = {
    hours: HoursSection,
    "find-us": FindUsSection,
    "price-list": PriceListSection,
    packages: PackagesSection,
    reviews: ReviewsSection,
    "before-after": BeforeAfterSection,
    "quote-form": QuoteFormSection,
    accreditations: AccreditationsSection,
    "how-it-works": HowItWorksSection,
    team: TeamSection,
    numbers: NumbersSection,
    "quick-tasks": QuickTasksSection,
    "cover-check": CoverCheckSection,
    newsletter: NewsletterSection,
    "latest-posts": LatestPostsSection,
    "our-work": OurWorkSection,
    backCard: BackCardSection,
    // Group B (S2) is below: S2_SECTIONS / s2Component. HomePage asks S2 first.
    // SH2 adds: product-list, category-list, featured-picks, collection-spotlight, live-price, see-in-shop.
};

// Today's types: their v2 layouts and component.
export const V2_REFRESH = {
    cta: {layouts: V2_REFRESH_LAYOUTS.cta, component: CtaV2},
    contact: {layouts: V2_REFRESH_LAYOUTS.contact, component: ContactV2},
    faq: {layouts: V2_REFRESH_LAYOUTS.faq, component: FaqV2},
    links: {layouts: V2_REFRESH_LAYOUTS.links, component: LinksV2},
    download: {layouts: V2_REFRESH_LAYOUTS.download, component: DownloadV2},
    areas: {layouts: V2_REFRESH_LAYOUTS.areas, component: AreasV2},
};

export function v2Component(section) {
    if (!section || typeof section.type !== "string") return null;
    if (V2_SECTIONS[section.type]) return V2_SECTIONS[section.type];
    const r = V2_REFRESH[section.type];
    if (r && r.layouts.includes(section.layout)) return r.component;
    return null;
}

export {BeforeAfterSection};

// ---- Section group B (S2) ----------------------------------------------------------

// Section group B (WEB_V2_BUILD.md §2, workstream S2): the components and how
// HomePage picks them. New types map straight to a component that takes
// {section, variant}; the older Words, photo-and-words, what-we-offer and
// photos sections switch to their v2 component only when the studio saved
// blocks or a layout, so pages nobody has touched render exactly as today
// (Flat stays pixel-identical).

import WordsSection from "./v2/WordsSection.vue";
import SplitV2 from "./v2/SplitV2.vue";
import FeaturesV2 from "./v2/FeaturesV2.vue";
import PhotosV2 from "./v2/PhotosV2.vue";
import BentoSection from "./v2/BentoSection.vue";
import TabsSection from "./v2/TabsSection.vue";
import RailSection from "./v2/RailSection.vue";
import AccordionPhoto from "./v2/AccordionPhoto.vue";
import StorySection from "./v2/StorySection.vue";
import StatementSection from "./v2/StatementSection.vue";
import MarqueeSection from "./v2/MarqueeSection.vue";
import VideoSection from "./v2/VideoSection.vue";
import CaseStudySection from "./v2/CaseStudySection.vue";
import PromiseSection from "./v2/PromiseSection.vue";
import CompareSection from "./v2/CompareSection.vue";
import EstimatorSection from "./v2/EstimatorSection.vue";
import PostV2 from "./v2/PostV2.vue";
import ProjectV2 from "./v2/ProjectV2.vue";
import PortfolioV2 from "./v2/PortfolioV2.vue";
import {s2UsesV2} from "@/utils/s2Pick.js";

// New types: the section map entries (type → component).
export const S2_SECTIONS = {
    bento: BentoSection,
    tabs: TabsSection,
    rail: RailSection,
    "accordion-photo": AccordionPhoto,
    story: StorySection,
    statement: StatementSection,
    marquee: MarqueeSection,
    video: VideoSection,
    "case-study": CaseStudySection,
    promise: PromiseSection,
    compare: CompareSection,
    estimator: EstimatorSection,
    "post-v2": PostV2,
    "project-v2": ProjectV2,
    // The v2 Portfolio page's projects (workstream PX).
    "portfolio-v2": PortfolioV2,
};

// Existing types that have a v2 component when the section asks for it.
const UPGRADES = {content: WordsSection, split: SplitV2, features: FeaturesV2, "image-grid": PhotosV2};

// The component for a section, or null to fall back to the existing map.
export function s2Component(section) {
    if (!section) return null;
    if (S2_SECTIONS[section.type]) return S2_SECTIONS[section.type];
    if (UPGRADES[section.type] && s2UsesV2(section)) return UPGRADES[section.type];
    return null;
}

// Props for an S2 component: the whole section plus where it sits.
export function s2Props(section, {index = 0, pageData = null} = {}) {
    return {
        section,
        variant: index % 2 === 0 ? 1 : 2,
        ...(section.type === "content" || section.type === "post-v2" ? {newsletterLink: pageData?.site?.newsletter?.link || ""} : {}),
        ...(section.type === "post-v2" ? {products: section.products || {}} : {}),
    };
}
